import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlannerConfig } from '../shared/contracts.js';
import type { TripReply, TripSummary } from '../shared/trip-api.js';
import { newTrip, newTripId, newSubtripId, nextTripName } from '../shared/trips.js';
import { activeTripId, clearLegacyTrips, rememberActiveTrip } from './browser-storage.js';
import { tripApi } from './trip-api.js';
import { TripEditor } from './tour-plans.js';
import { track } from './telemetry.js';

export function useTrips(key: string, config: PlannerConfig | undefined) {
  const api = useMemo(() => tripApi(key), [key]);
  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [editor, setEditor] = useState<TripEditor>();
  const current = useRef<TripEditor | undefined>(undefined);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const openVersion = useRef(0);
  const listVersion = useRef(0);
  const refreshRef = useRef<() => Promise<void>>(async () => {});

  const install = useCallback((reply: TripReply) => {
    if (!config) return;
    current.current?.dispose();
    const next: TripEditor = new TripEditor(reply.trip, reply, config, api, () => { void refreshRef.current(); }, () => {
      if (current.current !== next) return;
      next.dispose(); current.current = undefined; setEditor(undefined);
      setNotice('The open trip was deleted on another device.');
      void refreshRef.current();
    });
    current.current = next;
    setEditor(next);
    rememberActiveTrip(reply.trip.id);
    void next.start();
  }, [api, config]);

  const draft = useCallback((list: TripSummary[]) => {
    const trip = newTrip(newTripId(), nextTripName(list.map((trip) => trip.name)), newSubtripId());
    if (config) trip.subtrips[0]!.extraMinutes = Math.min(trip.subtrips[0]!.extraMinutes, config.maxExtraMinutes);
    const time = new Date().toISOString();
    install({ trip, revision: 0, createdAt: time, updatedAt: time, plans: {} });
  }, [install, config]);

  const open = useCallback(async (id: string) => {
    if (current.current?.state.deleting) return;
    const version = ++openVersion.current;
    try {
      await current.current?.saved();
      const reply = await api.get(id);
      await current.current?.saved();
      if (version !== openVersion.current) return;
      install(reply);
      track('trip-opened', { tripId: id, detail: { subtrips: reply.trip.subtrips.length } });
    } catch (error) { if (version === openVersion.current) setError(error instanceof Error ? error.message : String(error)); }
  }, [api, install]);

  const refresh = useCallback(async () => {
    if (!config) return;
    const version = ++listVersion.current;
    try {
      const { trips: list } = await api.list();
      if (version !== listVersion.current) return;
      setTrips(list);
      setError(undefined);
      const opened = current.current;
      if (opened?.state.deleting) return;
      if (!opened) {
        const wanted = activeTripId();
        const next = list.find((trip) => trip.id === wanted) ?? list[0];
        if (next) await open(next.id); else draft(list);
      } else if (opened.state.status.revision > 0 && !list.some((trip) => trip.id === opened.state.trip.id)) {
        // A first PUT can finish before its list refresh. Never discard a pending writer.
        if (opened.state.save.status === 'saving' || opened.state.save.status === 'unsaved') return;
        setNotice('The open trip was deleted on another device.');
        if (list[0]) await open(list[0].id); else draft(list);
      } else {
        const summary = list.find((trip) => trip.id === opened.state.trip.id);
        if (summary && (opened.state.error || summary.revision !== opened.state.status.revision || JSON.stringify(summary.plans) !== JSON.stringify(opened.state.status.plans) || JSON.stringify(summary.planning) !== JSON.stringify(opened.state.status.planning))) await opened.refresh();
      }
    } catch (error) { setError(error instanceof Error ? error.message : String(error)); }
  }, [api, config, draft, open]);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);
  useEffect(() => {
    clearLegacyTrips();
    void refresh();
    return () => { openVersion.current++; listVersion.current++; current.current?.dispose(); current.current = undefined; };
  }, [refresh]);

  const create = async () => {
    if (current.current?.state.deleting) return;
    try { await current.current?.saved(); }
    catch (error) { setError(error instanceof Error ? error.message : String(error)); return; }
    openVersion.current++;
    draft(trips);
    track('trip-created', { tripId: current.current?.state.trip.id });
  };
  const remove = async () => {
    const opened = current.current;
    if (!opened) return;
    const id = opened.state.trip.id;
    try {
      // Do not let an in-flight PUT recreate a deleted trip afterwards.
      await opened.prepareDelete();
      await api.remove(id);
      if (current.current !== opened) return;
      opened.dispose(); current.current = undefined; setEditor(undefined);
      track('trip-deleted', { tripId: id });
      const { trips: list } = await api.list();
      setTrips(list);
      if (list[0]) await open(list[0].id); else draft(list);
    } catch (error) { opened.cancelDelete(); setError(error instanceof Error ? error.message : String(error)); }
  };
  /** Pairing put this browser's trips together with the phone's. */
  const paired = () => {
    openVersion.current++; listVersion.current++;
    current.current?.dispose(); current.current = undefined; setEditor(undefined);
    setNotice('Paired. This browser and your phone now share their trips.');
    void refresh();
  };
  return { trips, editor, error, notice, dismissNotice: () => setNotice(undefined), open, create, remove, refresh, paired };
}
